import React, { Suspense, useContext, useEffect, useState } from 'react';

import defaultStylesCss from './assets/styles/default.styles.scss?inline';
import iconFontCss from './assets/styles/icon-font.min.scss?inline';
import porscheStylesCss from './assets/styles/porsche.styles.scss?inline';
import brandStylesCss from './assets/styles/styles.scss?inline';

const injectStyle = (id: string, css: string) => {
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = css;
    document.head.appendChild(style);
};

import {
    bronsonThemes,
    defaultBronsonTheme,
    defaultFinancialProductTypeId,
    defaultMinimumValue,
    minimumDownpaymentByBrandFinancialProduct,
    porscheThemeText,
    propsToCDNLink,
} from './app.constants';
import { AppService } from './App.service';
import { SkeletonLoader } from './components';
import { ProvidersContext } from './contexts/providers.context';
import { CarType } from './utils/enums';
import { ErrorUtilClass } from './utils/error.class';
import {
    FinancialProduct,
    ProductPricingParams,
    SimulationData,
    StorefrontModel,
    VehicleModel,
} from './utils/interfaces';
import {
    buildIAnItemSelectorArray,
    getPropValueFromVehicleModelsTrimLineDetails,
} from './utils/util.class';
import { FCalculatorError } from './Views';
import { carDataDoesNotMatch } from './Views/CalculatorError/error.constants';

export const App: React.FC = () => {
    const errorUtilClass = new ErrorUtilClass();
    const config = useContext(ProvidersContext);
    const isAudiUsed = !!config.price && config.brand.toLowerCase() === 'audi';
    const isPFA = config.isPFA === false ? config.isPFA : true;
    const appService = new AppService();
    const [financialProducts, setFinancialProducts] = useState<
        Array<FinancialProduct>
    >([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [vehicleModels, setVehicleModels] = useState<Array<VehicleModel>>([]);
    const [simulation, setSimulationData] = useState<SimulationData>({
        id: 0,
        installmentAmount: 0,
        commissionChargeAmount: 0,
    });
    const [externalStoreFront, setExternalStoreFront] =
        useState<StorefrontModel>({} as StorefrontModel);
    const [loader, setLoader] = useState<boolean>(true);

    useEffect(() => {
        /**
         * This method is to fetch all necessary data of the brand for the calculator
         * to show proper information
         */
        const getDataForBrand = async () => {
            try {
                const type = isAudiUsed ? CarType.USED : CarType.NEW;

                const yearModel = isAudiUsed ? config.year : undefined;
                const financialProductsResponse: Array<FinancialProduct> =
                    await appService
                        .fetchFinancialProducts(
                            config.brand,
                            type,
                            isPFA,
                            yearModel,
                        )
                        .then((response) => response.data.financialProducts)
                        .then((products) =>
                            products
                                .map(filterFinancialRule)
                                .filter(
                                    (product) => !/Leasing/i.test(product.name),
                                ),
                        )
                        .then(setFinancialProductsOrder)
                        .then(errorUtilClass.validateFinancialProductsData);

                const vehicleModelsResponse = await appService
                    .fetchVehicleModels(config.brand, type, isPFA)
                    .then((response) => response.data.vehicleModels)
                    .then((vehicles) =>
                        errorUtilClass.validateVehiclesCatalogData(
                            vehicles,
                            type,
                        ),
                    );

                const trimLineId = getPropValueFromVehicleModelsTrimLineDetails(
                    vehicleModelsResponse,
                    config,
                    'id',
                    isAudiUsed,
                );

                if (config.externalId && trimLineId) {
                    const externalDataBrand = await appService
                        .fetchExternalData(config.brand, config.externalId)
                        .then((response) => response.data);
                    setExternalStoreFront(externalDataBrand);
                }

                const productDuration = buildIAnItemSelectorArray(
                    financialProductsResponse[0].productDurations,
                )[0];

                setVehicleModels(vehicleModelsResponse);
                setFinancialProducts(financialProductsResponse);

                if (!trimLineId) {
                    throw new Error(carDataDoesNotMatch);
                } else {
                    const body: ProductPricingParams = {
                        financialProductDurationId: productDuration.id,
                        vehicleModelTrimLineId: trimLineId || 0,
                        downPaymentPercentage:
                            productDuration.minimumDownPayment || 0,
                    };
                    if (isAudiUsed) {
                        body.retailPrice = config.price;
                        body.modelYear = config.year;
                    }
                    const simulationResponse = await appService
                        .fetchSimulation(config.brand, isPFA, body)
                        .then((response) => response.data)
                        .catch(() => setLoader(false));

                    setSimulationData(simulationResponse);
                }
            } catch (error) {
                // @ts-ignore
                setErrorMessage(error.message);
                setLoader(false);
            }
            setLoader(false);
        };

        getDataForBrand();

        /**
         * This conditional add the stylesheet only if the brand
         * selected is porsche.
         */
        if (config.brand.toLowerCase() === porscheThemeText) {
            injectStyle('mfc-porsche-styles', porscheStylesCss);
        } else {
            injectStyle('mfc-default-styles', defaultStylesCss);
        }
    }, []);

    /**
     * This method validates that every product minimal percentage is 20% if it has any
     * smaller down payment it will be omitted
     * @param product
     * @returns {FinancialProduct}
     */
    const filterFinancialRule = (
        product: FinancialProduct,
    ): FinancialProduct => {
        const minimum =
            minimumDownpaymentByBrandFinancialProduct.porsche.find(
                (item) =>
                    item.financialProductTypeId ===
                    product.financialProductTypeId,
            )?.minimum || defaultMinimumValue;

        return {
            ...product,
            productDurations: product.productDurations.map((duration) => {
                return {
                    ...duration,
                    minimumDownPayment: minimum,
                };
            }),
        };
    };

    /**
     * This method only job is to reorder the financial products provided by the API with the Premium
     * credit as the first element to be show.
     * @param financialProductsRes financial products from API
     * @returns {Array<FinancialProduct>} New ordered array of financial products
     */
    const setFinancialProductsOrder = (
        financialProductsRes: Array<FinancialProduct>,
    ): Array<FinancialProduct> => {
        const premiumIndex = financialProductsRes.findIndex(
            (financialProd) =>
                financialProd.financialProductTypeId ===
                defaultFinancialProductTypeId,
        );
        const newFinancialProdsOrder: Array<FinancialProduct> = [];
        // Assuring that there is a premium credit product it will be set first in the array
        if (premiumIndex !== -1) {
            const premFinancialProd = financialProductsRes.slice(
                premiumIndex,
                premiumIndex + 1,
            )[0];
            newFinancialProdsOrder.push(premFinancialProd);
            financialProductsRes.splice(premiumIndex, 1);
        }
        newFinancialProdsOrder.push(...financialProductsRes);

        return newFinancialProdsOrder;
    };

    /**
     * This function get the specific cdn url for the
     * brand selected.
     * @param theme
     */
    const getBrandCDNStyles = (theme: string): string => {
        const brandTheme = bronsonThemes.find((item) =>
            item.theme.toLowerCase().includes(theme.toLowerCase()),
        );
        return brandTheme && brandTheme.cdn
            ? brandTheme.cdn
            : defaultBronsonTheme;
    };

    /**
     * This function create a promise with an interval
     * that is resolved when the loader value is false.
     * The loader value will be change when the catalogs
     * services finished of load the data.
     */
    const isCatalogDataUploaded = (): Promise<void> => {
        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if (!loader) {
                    resolve();
                    clearInterval(interval);
                }
            }, 1);
        });
    };

    /**
     * This function add the cdn selected as link into the
     * head tag.
     */
    const setTheCDNToBrandStyles = (): Promise<void> => {
        return new Promise((resolve) => {
            const addLinkStyles = () => {
                const head = document.getElementsByTagName(
                    propsToCDNLink.parent,
                )[0];
                const timeout = 500;

                const cdn = getBrandCDNStyles(config.brand);
                if (config.brand.toLowerCase() === porscheThemeText) {
                    injectStyle('mfc-icon-font', iconFontCss);
                    injectStyle('mfc-brand-styles', brandStylesCss);
                    resolve();
                } else if (head) {
                    const link = document.createElement(
                        propsToCDNLink.type,
                    ) as HTMLLinkElement;
                    link.id = propsToCDNLink.id;
                    link.rel = propsToCDNLink.rel;
                    link.href = cdn;
                    link.onload = () => resolve();
                    link.onerror = () => resolve();
                    head.appendChild(link);
                } else {
                    setTimeout(() => addLinkStyles(), timeout);
                }
            };

            addLinkStyles();
        });
    };

    /**
     * This React lazy function return the financial
     * calculator component when then brand stylesheet
     * is uploaded. While the cdn is downloaded the
     * skeleton loader is rendered.
     */
    const FCalculator = React.lazy(async () => {
        return Promise.all([setTheCDNToBrandStyles(), isCatalogDataUploaded()])
            .then(() => import('./Views'))
            .then(({ FinancialCalculator }) => ({
                default: FinancialCalculator,
            }))
            .catch((error) => {
                throw new Error(error);
            });
    });

    return (
        <div className='dh-mfc'>
            <Suspense fallback={<SkeletonLoader />}>
                {errorMessage === '' ? (
                    <FCalculator
                        {...{
                            financialProducts,
                            vehicleModels,
                            simulation,
                            externalStoreFront,
                        }}
                    />
                ) : (
                    <FCalculatorError
                        config={config}
                        error={errorMessage}
                        vehicleModels={vehicleModels}
                    />
                )}
            </Suspense>
        </div>
    );
};
